import React, {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import {
  Box,
  Modal,
  Stepper,
  Group,
  Button,
  LoadingOverlay,
  Stack,
  Text,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useApp } from "../../../context/AppContext";
import { useSalon } from "../../../context/SalonContext";
import { useServiceAdjustmentForms } from "../../../context/ServiceAdjustmentFormsContext";
import { useServiceRequiredForms } from "../../../context/ServiceRequiredFormsContext";
import { useServicePreAppointmentDocuments } from "../../../context/ServicePreAppointmentDocumentsContext";
import { useServicePostAppointment } from "../../../context/ServicePostAppointmentContext";
import ServiceInformationStep from "./ServiceInformationStep";
import AdditionalInformationStep from "./AdditionalInformationStep";
import StaffSelectionStep from "./StaffSelectionStep";
import AdjustmentFormsStep from "./AdjustmentFormsStep";
import { extractDayFlags } from "./dayAvailability";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import UpdateStaffServicesModal from "../../common/UpdateStaffServicesModal";
import {
  MessageSettings,
  DEFAULT_MESSAGE_SETTINGS,
  hasNonDefaultMessageSettings,
} from "../../../types/messageTemplates";
import { PendingRequiredForm } from "../../../types/formTemplates";
import { PendingPreAppointmentDocument } from "../../../types/servicePreAppointment";
import {
  DEFAULT_POST_APPOINTMENT_SETTINGS,
  PostAppointmentSettings,
  PendingPostAppointmentMessage,
} from "../../../types/servicePostAppointment";
import { getApiUrl, API_CONFIG } from "../../../config/api";
import { notifyError } from "../../../utils/notify";
// @ts-ignore
import _ from "lodash";
// @ts-ignore
import { filterActiveStaff } from "../../../utils/staffUtils";

interface ServiceFormWizardProps {
  opened: boolean;
  onClose: () => void;
  onSuccess: (serviceData: any) => void;
  initialData?: any;
  isEditing?: boolean;
  serviceTypes?: any[] | null;
}

export default function ServiceFormWizard({
  opened,
  onClose,
  onSuccess,
  initialData,
  isEditing = false,
  serviceTypes = null,
}: ServiceFormWizardProps) {
  const {
    createServices,
    updateServices,
    deleteStaffService,
    getTaxRates,
    setSalonServiceTaxRates,
    fetchWithAuth,
  } = useApp();
  const { businessInfo, employees } = useSalon() as any;
  const activeEmployees = useMemo(
    () => filterActiveStaff(employees),
    [employees],
  );
  const {
    adjustmentForms,
    connectFormToServices,
    disconnectFormFromService,
    formJunctions,
  } = useServiceAdjustmentForms();
  const { linkRequiredForm } = useServiceRequiredForms();
  const { linkPreAppointmentDocument } = useServicePreAppointmentDocuments();
  const {
    updateSettings: updatePostAppointmentSettings,
    createMessage: createPostAppointmentMessage,
  } = useServicePostAppointment();
  const isMobile = useMediaQuery("(max-width: 768px)");

  // Determine if we should show the adjustment forms step
  const showAdjustmentFormsStep =
    adjustmentForms.filter((form: any) => form.is_active).length > 0;
  const totalSteps = showAdjustmentFormsStep ? 4 : 3;

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showCloseConfirmation, setShowCloseConfirmation] = useState(false);
  const [updateStaffModalOpened, setUpdateStaffModalOpened] = useState(false);
  const [changedFields, setChangedFields] = useState<string[]>([]);
  const [pendingSaveAction, setPendingSaveAction] = useState<
    (() => Promise<void>) | null
  >(null);

  // Form data state
  const initialFormData = {
    id: initialData?.id || null,
    name: initialData?.name || "",
    type: initialData?.service_type || "",
    description: initialData?.description || "",
    duration: `${initialData?.default_base_duration_mins ?? ""}`,
    processingTime: `${initialData?.default_processing_time_mins ?? "0"}`,
    finishingTime: `${initialData?.default_finishing_time_mins ?? "0"}`,
    bufferTime: `${initialData?.default_buffer_time_mins ?? "0"}`,
    enableDoubleBooking: initialData?.enable_double_booking ?? true,
    price: initialData?.default_price ?? null,
    price_basis: initialData?.price_basis ?? "per_service",
    startsAt: initialData?.default_is_variable_price || false,
    default_calendar_event_color:
      initialData?.default_calendar_event_color || "#3174ad",
    is_self_servicable:
      initialData?.default_is_self_servicable ?? (isEditing ? false : true),
    faron__is_private_room: initialData?.faron__is_private_room || false,
    // Per-service Card-on-File requirement (migrated from salon-wide setting)
    require_card_to_book:
      initialData?.require_card_to_book ??
      initialData?.default_require_card_to_book ??
      false,
    // Salon-level day-of-week availability (defaults across staff)
    ...extractDayFlags(initialData),
    employeeSettings: (activeEmployees || []).reduce(
      (acc: any, employee: any) => {
        // Find if this employee has an existing staff service
        const staffService = initialData?.servicing_staff?.find(
          (ss: any) => ss.staff_id === employee.id,
        );

        if (staffService) {
          // Employee is assigned to this service
          acc[employee.id] = {
            id: staffService.id,
            enabled: true,
            price: staffService.price,
            duration: `${staffService.base_duration_mins ?? staffService.duration_mins}`,
            processingTime: `${staffService.processing_time_mins ?? 0}`,
            finishingTime: `${staffService.finishing_time_mins ?? 0}`,
            bufferTime: `${staffService.buffer_time_mins ?? 0}`,
            startsAt: staffService.is_variable_price,
            isOpen: true,
            is_self_servicable: staffService.is_self_servicable,
            // Per-staff day-of-week flags (fall back to all days allowed)
            ...extractDayFlags(staffService),
          };
        } else {
          // Employee is not assigned to this service - default to disabled
          acc[employee.id] = {
            id: null,
            enabled: false,
            price: initialData?.default_price ?? null,
            duration: `${initialData?.default_base_duration_mins ?? ""}`,
            processingTime: `${initialData?.default_processing_time_mins ?? 0}`,
            finishingTime: `${initialData?.default_finishing_time_mins ?? 0}`,
            bufferTime: `${initialData?.default_buffer_time_mins ?? 0}`,
            startsAt: initialData?.default_is_variable_price || false,
            isOpen: false,
            is_self_servicable:
              initialData?.default_is_self_servicable ?? false,
            // Default to the salon-level available days
            ...extractDayFlags(initialData),
          };
        }
        return acc;
      },
      {},
    ),
  };

  const [formData, setFormData] = useState(initialFormData);
  // Stable snapshot of the form's baseline; updated whenever the modal opens
  // so the isDirty check and close-reset don't hold a stale object or fight
  // per-render recreation of initialFormData.
  const initialFormDataRef = useRef(initialFormData);

  // Adjustment forms state
  const [selectedAdjustmentFormIds, setSelectedAdjustmentFormIds] = useState<
    string[]
  >([]);

  // Phase 2 tax rate junctions. Array of integer tax_rate.id PKs; populated
  // on modal open by looking up the current linkage and persisted after the
  // main service save via setSalonServiceTaxRates.
  const [selectedTaxRateIds, setSelectedTaxRateIds] = useState<number[]>([]);

  const [pendingMessageSettings, setPendingMessageSettings] =
    useState<MessageSettings>(DEFAULT_MESSAGE_SETTINGS);
  const [pendingRequiredForms, setPendingRequiredForms] = useState<
    PendingRequiredForm[]
  >([]);
  const [pendingPreAppointmentDocuments, setPendingPreAppointmentDocuments] =
    useState<PendingPreAppointmentDocument[]>([]);
  const [pendingPostAppointmentSettings, setPendingPostAppointmentSettings] =
    useState<PostAppointmentSettings>(DEFAULT_POST_APPOINTMENT_SETTINGS);
  const [pendingPostAppointmentMessages, setPendingPostAppointmentMessages] =
    useState<PendingPostAppointmentMessage[]>([]);

  // Reset form data when modal opens or initialData changes
  useEffect(() => {
    if (opened) {
      const newFormData = {
        id: initialData?.id || null,
        name: initialData?.name || "",
        type: initialData?.service_type || "",
        description: initialData?.description || "",
        duration: `${initialData?.default_base_duration_mins ?? ""}`,
        processingTime: `${initialData?.default_processing_time_mins ?? "0"}`,
        finishingTime: `${initialData?.default_finishing_time_mins ?? "0"}`,
        bufferTime: `${initialData?.default_buffer_time_mins ?? "0"}`,
        enableDoubleBooking: initialData?.enable_double_booking ?? true,
        price: initialData?.default_price ?? null,
        price_basis: initialData?.price_basis ?? "per_service",
        startsAt: initialData?.default_is_variable_price || false,
        default_calendar_event_color:
          initialData?.default_calendar_event_color || "#3174ad",
        is_self_servicable:
          initialData?.default_is_self_servicable ?? (isEditing ? false : true),
        faron__is_private_room: initialData?.faron__is_private_room || false,
        require_card_to_book:
          initialData?.require_card_to_book ??
          initialData?.default_require_card_to_book ??
          false,
        // Salon-level day-of-week availability (defaults across staff)
        ...extractDayFlags(initialData),
        employeeSettings: (activeEmployees || []).reduce(
          (acc: any, employee: any) => {
            // Find if this employee has an existing staff service
            const staffService = initialData?.servicing_staff?.find(
              (ss: any) => ss.staff_id === employee.id,
            );

            if (staffService) {
              // Employee is assigned to this service
              acc[employee.id] = {
                id: staffService.id,
                enabled: true,
                price: staffService.price,
                duration: `${staffService.base_duration_mins ?? staffService.duration_mins}`,
                processingTime: `${staffService.processing_time_mins ?? 0}`,
                finishingTime: `${staffService.finishing_time_mins ?? 0}`,
                bufferTime: `${staffService.buffer_time_mins ?? 0}`,
                startsAt: staffService.is_variable_price,
                isOpen: true,
                is_self_servicable: staffService.is_self_servicable,
                // Per-staff day-of-week flags (fall back to all days allowed)
                ...extractDayFlags(staffService),
              };
            } else {
              // Employee is not assigned to this service - default to disabled
              acc[employee.id] = {
                id: null,
                enabled: false,
                price: initialData?.default_price ?? null,
                duration: `${initialData?.default_base_duration_mins ?? ""}`,
                processingTime: `${initialData?.default_processing_time_mins ?? 0}`,
                finishingTime: `${initialData?.default_finishing_time_mins ?? 0}`,
                bufferTime: `${initialData?.default_buffer_time_mins ?? 0}`,
                startsAt: initialData?.default_is_variable_price || false,
                isOpen: false,
                is_self_servicable:
                  initialData?.default_is_self_servicable ?? false,
                // Default to the salon-level available days
                ...extractDayFlags(initialData),
              };
            }
            return acc;
          },
          {},
        ),
      };
      initialFormDataRef.current = newFormData;
      setFormData(newFormData);
      setActiveStep(0); // Reset to first step
    }
  }, [opened, initialData, isEditing, activeEmployees]);

  // Load initial adjustment forms if editing
  useEffect(() => {
    if (opened && isEditing && initialData?.id) {
      const connectedForms = formJunctions
        .filter((junction) => junction.salon_service_id === initialData.id)
        .map((junction) => junction.service_adjustment_form_id);
      setSelectedAdjustmentFormIds(connectedForms);
    } else if (opened && !isEditing) {
      setSelectedAdjustmentFormIds([]);
      setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
      setPendingRequiredForms([]);
      setPendingPostAppointmentSettings(DEFAULT_POST_APPOINTMENT_SETTINGS);
      setPendingPostAppointmentMessages([]);
    }
  }, [opened, isEditing, initialData?.id, formJunctions]);

  // Phase 2: load currently-linked tax rates when the modal opens for an
  // existing service. We walk the salon's tax rate list and pick out rows
  // whose salon_service_ids includes this service's UUID.
  useEffect(() => {
    if (!opened) {
      setSelectedTaxRateIds([]);
      return;
    }
    if (!isEditing || !initialData?.id || !businessInfo?.id) {
      setSelectedTaxRateIds([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const rates = await getTaxRates(businessInfo.id);
        if (cancelled) return;
        const linked = (Array.isArray(rates) ? rates : [])
          .filter((r: any) => r.salon_service_ids?.includes(initialData.id))
          .map((r: any) => r.id);
        setSelectedTaxRateIds(linked);
      } catch (err) {
        console.error("Failed to load current tax rates for service:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [opened, isEditing, initialData?.id, businessInfo?.id, getTaxRates]);

  // Check if form is dirty
  const initialAdjustmentFormIds = isEditing
    ? formJunctions
        .filter((j) => j.salon_service_id === initialData?.id)
        .map((j) => j.service_adjustment_form_id)
    : [];

  const isDirty =
    !_.isEqual(formData, initialFormDataRef.current) ||
    !_.isEqual(
      selectedAdjustmentFormIds.sort(),
      initialAdjustmentFormIds.sort(),
    ) ||
    hasNonDefaultMessageSettings(pendingMessageSettings) ||
    pendingRequiredForms.length > 0 ||
    pendingPreAppointmentDocuments.length > 0 ||
    pendingPostAppointmentSettings.master_enabled ||
    pendingPostAppointmentMessages.length > 0;

  const handleClose = useCallback(() => {
    onClose();
    setActiveStep(0);
    setFormData(initialFormDataRef.current);
    setSelectedAdjustmentFormIds([]);
    setPendingMessageSettings(DEFAULT_MESSAGE_SETTINGS);
    setPendingRequiredForms([]);
    setPendingPreAppointmentDocuments([]);
    setPendingPostAppointmentSettings(DEFAULT_POST_APPOINTMENT_SETTINGS);
    setPendingPostAppointmentMessages([]);
    setErrors({});
    setShowCloseConfirmation(false);
  }, [onClose]);

  const handleCloseAttempt = useCallback(() => {
    if (isDirty) {
      setShowCloseConfirmation(true);
    } else {
      handleClose();
    }
  }, [isDirty, handleClose]);

  const validateStep = useCallback(
    (step: number): boolean => {
      const newErrors: Record<string, string> = {};

      if (step === 0) {
        // Validate service information
        if (!formData.name?.trim()) {
          newErrors.name = "Service name is required";
        } else if (formData.name.length > 256) {
          newErrors.name = "Service name must be 256 characters or fewer";
        }
        if (!formData.type?.trim()) {
          newErrors.type = "Service type is required";
        } else if (formData.type.length > 128) {
          newErrors.type = "Service type must be 128 characters or fewer";
        }
        if (formData.description && formData.description.length > 8000) {
          newErrors.description =
            "Description is too long; shorten formatting or text";
        }
        if (!formData.duration || Number(formData.duration) <= 0) {
          newErrors.duration = "Valid duration is required";
        }
        if (formData.price == null || formData.price < 0) {
          newErrors.price = "Valid price is required";
        }
        if (!/^#[0-9A-F]{6}$/i.test(formData.default_calendar_event_color)) {
          newErrors.default_calendar_event_color = "Valid hex color required";
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [formData],
  );

  const handleNext = useCallback(() => {
    if (validateStep(activeStep)) {
      setActiveStep((prev) => Math.min(prev + 1, totalSteps - 1));
    }
  }, [activeStep, totalSteps, validateStep]);

  const handleBack = useCallback(() => {
    setActiveStep((prev) => Math.max(prev - 1, 0));
  }, []);

  // Helper function to get changed default fields
  const getChangedDefaultFields = (initial: any, current: any): string[] => {
    const changedFields: string[] = [];

    if (
      parseInt(initial.duration || "0") !== parseInt(current.duration || "0")
    ) {
      changedFields.push("Base Duration");
    }
    if (
      parseInt(initial.processingTime ?? "0") !==
      parseInt(current.processingTime ?? "0")
    ) {
      changedFields.push("Processing Time");
    }
    if (
      parseInt(initial.finishingTime ?? "0") !==
      parseInt(current.finishingTime ?? "0")
    ) {
      changedFields.push("Finishing Time");
    }
    if (
      parseInt(initial.bufferTime ?? "0") !==
      parseInt(current.bufferTime ?? "0")
    ) {
      changedFields.push("Buffer Time");
    }
    if (initial.price !== current.price) {
      changedFields.push("Price");
    }
    if (initial.is_self_servicable !== current.is_self_servicable) {
      changedFields.push("Show in online booking");
    }
    if (initial.faron__is_private_room !== current.faron__is_private_room) {
      changedFields.push("Private Room");
    }

    return changedFields;
  };

  // Helper function to check if staff service has customization
  const hasStaffCustomization = (
    staffService: any,
    serviceDefaults: any,
  ): boolean => {
    const staffPrice = staffService.price;
    const staffDuration = parseInt(staffService.duration || "0");
    const staffProcessingTime = parseInt(staffService.processingTime ?? "0");
    const staffFinishingTime = parseInt(staffService.finishingTime ?? "0");
    const staffBufferTime = parseInt(staffService.bufferTime ?? "0");
    const staffIsSelfServicable = staffService.is_self_servicable;

    const defaultPrice = serviceDefaults.price;
    const defaultDuration = parseInt(serviceDefaults.duration || "0");
    const defaultProcessingTime = parseInt(
      serviceDefaults.processingTime ?? "0",
    );
    const defaultFinishingTime = parseInt(serviceDefaults.finishingTime ?? "0");
    const defaultBufferTime = parseInt(serviceDefaults.bufferTime ?? "0");
    const defaultIsSelfServicable = serviceDefaults.is_self_servicable;

    return (
      staffPrice !== defaultPrice ||
      staffDuration !== defaultDuration ||
      staffProcessingTime !== defaultProcessingTime ||
      staffFinishingTime !== defaultFinishingTime ||
      staffBufferTime !== defaultBufferTime ||
      staffIsSelfServicable !== defaultIsSelfServicable
    );
  };

  const formatResponse = (response: any[]) => {
    const formattedResponse = {
      id: response[0].id,
      name: response[0].name,
      service_type: response[0].service_type,
      description: response[0].description,
      default_base_duration_mins: response[0].default_base_duration_mins,
      default_processing_time_mins: response[0].default_processing_time_mins,
      default_finishing_time_mins: response[0].default_finishing_time_mins,
      default_buffer_time_mins: response[0].default_buffer_time_mins,
      enable_double_booking: response[0].enable_double_booking,
      default_price: response[0].default_price,
      price_basis: response[0].price_basis,
      default_is_variable_price: response[0].default_is_variable_price,
      default_calendar_event_color: response[0].default_calendar_event_color,
      default_is_self_servicable: response[0].default_is_self_servicable,
      faron__is_private_room: response[0].faron__is_private_room,
      require_card_to_book:
        response[0].require_card_to_book ??
        response[0].default_require_card_to_book ??
        false,
      ...extractDayFlags(response[0]),
      servicing_staff: response.slice(1).map((staffService: any) => {
        const empId = staffService.staff_id
          ? staffService.staff_id
          : Object.entries(formData.employeeSettings).find(
              ([_, settings]: any) => settings.id === staffService.id,
            )?.[0];
        const emp = (employees || []).find((emp: any) => emp.id === empId);
        return {
          base_duration_mins: staffService.base_duration_mins,
          processing_time_mins: staffService.processing_time_mins,
          finishing_time_mins: staffService.finishing_time_mins,
          buffer_time_mins: staffService.buffer_time_mins,
          duration_mins: staffService.duration_mins, // Keep for backward compatibility
          id: staffService.id,
          is_variable_price: staffService.is_variable_price,
          price: staffService.price,
          staff_id: empId,
          staff_name: emp ? `${emp.first_name} ${emp.last_name}` : "",
          is_self_servicable: staffService.is_self_servicable,
          ...extractDayFlags(staffService),
        };
      }),
      price_range: {
        min: Math.min(
          ...response.slice(1).map((staffService: any) => staffService.price),
        ),
        max: Math.max(
          ...response.slice(1).map((staffService: any) => staffService.price),
        ),
      },
    };
    return formattedResponse;
  };

  const performSave = useCallback(
    async (updateOption?: "staff_without_customization" | "all_staff") => {
      setIsLoading(true);
      setErrors({});

      try {
        let serviceId = formData.id;
        let response: any;

        if (!isEditing) {
          // Create new service
          const createPayload = {
            salon_id: businessInfo?.id,
            services: [
              {
                name: formData.name,
                service_type: formData.type,
                description: formData.description,
                default_base_duration_mins: parseInt(formData.duration),
                default_processing_time_mins: parseInt(
                  formData.processingTime ?? "0",
                ),
                default_finishing_time_mins: parseInt(
                  formData.finishingTime ?? "0",
                ),
                default_buffer_time_mins: parseInt(formData.bufferTime ?? "0"),
                enable_double_booking: formData.enableDoubleBooking,
                default_price: formData.price,
                price_basis: formData.price_basis ?? "per_service",
                default_is_variable_price: formData.startsAt,
                default_calendar_event_color:
                  formData.default_calendar_event_color,
                default_is_self_servicable: formData.is_self_servicable,
                faron__is_private_room: formData.faron__is_private_room,
                require_card_to_book: formData.require_card_to_book ?? false,
                ...extractDayFlags(formData),
                staff_services: Object.entries(formData.employeeSettings)
                  .filter(([_, settings]: any) => settings.enabled)
                  .map(([empId, settings]: any) => ({
                    staff_id: empId,
                    price: settings.price,
                    base_duration_mins: parseInt(settings.duration),
                    processing_time_mins: parseInt(
                      settings.processingTime ?? "0",
                    ),
                    finishing_time_mins: parseInt(
                      settings.finishingTime ?? "0",
                    ),
                    buffer_time_mins: parseInt(settings.bufferTime ?? "0"),
                    is_variable_price: settings.startsAt,
                    is_self_servicable: settings.is_self_servicable,
                    ...extractDayFlags(settings),
                  })),
              },
            ],
          };

          response = await createServices(createPayload);
          serviceId = response[0].id;
        } else {
          // Update existing service
          // Determine which staff services to update based on option
          let staffServicesToUpdate: any[] = [];

          if (updateOption === "staff_without_customization") {
            // Only update service defaults, don't update any staff services
            staffServicesToUpdate = [];
          } else if (updateOption === "all_staff") {
            // Update all enabled staff services
            staffServicesToUpdate = Object.entries(formData.employeeSettings)
              .filter(([_, settings]: any) => settings.enabled && settings.id)
              .map(([_, settings]: any) => ({
                id: settings.id,
                price: formData.price, // Use new default
                base_duration_mins: parseInt(formData.duration), // Use new default
                processing_time_mins: parseInt(formData.processingTime ?? "0"), // Use new default
                finishing_time_mins: parseInt(formData.finishingTime ?? "0"), // Use new default
                buffer_time_mins: parseInt(formData.bufferTime ?? "0"), // Use new default
                is_variable_price: settings.startsAt, // Keep existing
                is_self_servicable: formData.is_self_servicable, // Use new default
                ...extractDayFlags(formData), // Use new salon-level default days
              }));
          } else {
            // Default behavior: update all existing staff services (preserve current behavior)
            staffServicesToUpdate = Object.entries(formData.employeeSettings)
              .filter(([_, settings]: any) => settings.enabled)
              .filter(
                ([empId, _]: any) => initialFormData.employeeSettings[empId],
              )
              .filter(([_, settings]: any) => settings.id) // Only include staff services with valid IDs
              .map(([_, settings]: any) => ({
                id: settings.id,
                price: settings.price,
                base_duration_mins: parseInt(settings.duration),
                processing_time_mins: parseInt(settings.processingTime ?? "0"),
                finishing_time_mins: parseInt(settings.finishingTime ?? "0"),
                buffer_time_mins: parseInt(settings.bufferTime ?? "0"),
                is_variable_price: settings.startsAt,
                is_self_servicable: settings.is_self_servicable,
                ...extractDayFlags(settings),
              }));
          }

          const updatePayload = {
            salon_services: [
              {
                id: formData.id,
                name: formData.name,
                service_type: formData.type,
                description: formData.description,
                default_base_duration_mins: parseInt(formData.duration),
                default_processing_time_mins: parseInt(
                  formData.processingTime ?? "0",
                ),
                default_finishing_time_mins: parseInt(
                  formData.finishingTime ?? "0",
                ),
                default_buffer_time_mins: parseInt(formData.bufferTime ?? "0"),
                enable_double_booking: formData.enableDoubleBooking,
                default_price: formData.price,
                price_basis: formData.price_basis ?? "per_service",
                default_is_variable_price: formData.startsAt,
                default_calendar_event_color:
                  formData.default_calendar_event_color,
                default_is_self_servicable: formData.is_self_servicable,
                faron__is_private_room: formData.faron__is_private_room,
                require_card_to_book: formData.require_card_to_book ?? false,
                ...extractDayFlags(formData),
              },
            ],
            staff_services: staffServicesToUpdate,
          };

          const createPayload = {
            salon_id: businessInfo?.id,
            staff_services: Object.entries(formData.employeeSettings)
              .filter(([_, settings]: any) => settings.enabled)
              .filter(([_, settings]: any) => !settings.id)
              .map(([empId, settings]: any) => ({
                salon_service_id: formData.id,
                staff_id: empId,
                price: settings.price,
                base_duration_mins: parseInt(settings.duration),
                processing_time_mins: parseInt(settings.processingTime ?? "0"),
                finishing_time_mins: parseInt(settings.finishingTime ?? "0"),
                buffer_time_mins: parseInt(settings.bufferTime ?? "0"),
                is_variable_price: settings.startsAt,
                is_self_servicable: settings.is_self_servicable,
                ...extractDayFlags(settings),
              })),
          };

          const updateResponse = await updateServices(updatePayload);
          const createResponse =
            createPayload.staff_services.length > 0
              ? await createServices(createPayload)
              : [];

          // Delete removed staff services
          const deletedStaffServices = Object.entries(formData.employeeSettings)
            .filter(([staffId, settings]: any) => {
              const initialSettings = initialFormData.employeeSettings[staffId];
              return (
                initialSettings?.id &&
                !settings.enabled &&
                initialSettings.enabled
              );
            })
            .map(([_, settings]: any) => settings.id);

          if (deletedStaffServices.length > 0) {
            await Promise.all(
              deletedStaffServices.map(async (id: string) => {
                await deleteStaffService(id);
              }),
            );
          }

          response = [...updateResponse, ...createResponse];
        }

        // Handle adjustment forms connections
        if (showAdjustmentFormsStep && serviceId) {
          const initialFormIds = formJunctions
            .filter((j) => j.salon_service_id === serviceId)
            .map((j) => j.service_adjustment_form_id);

          const formsToAdd = selectedAdjustmentFormIds.filter(
            (id) => !initialFormIds.includes(id),
          );
          const formsToRemove = initialFormIds.filter(
            (id) => !selectedAdjustmentFormIds.includes(id),
          );

          // Connect new forms
          for (const formId of formsToAdd) {
            await connectFormToServices(formId, [serviceId]);
          }

          // Disconnect removed forms
          for (const formId of formsToRemove) {
            const junction = formJunctions.find(
              (j) =>
                j.service_adjustment_form_id === formId &&
                j.salon_service_id === serviceId,
            );
            if (junction) {
              await disconnectFormFromService(
                junction.service_adjustment_form_junction_id,
              );
            }
          }
        }

        // Phase 2: persist the salon_service_tax_rates junction. One bulk
        // call; the backend wraps the DELETE/INSERT in tax_bulk_mode and
        // fires a single scoped line_items recalc.
        if (serviceId && businessInfo?.id) {
          try {
            await setSalonServiceTaxRates(
              businessInfo.id,
              serviceId,
              selectedTaxRateIds,
            );
          } catch (taxErr) {
            console.error(
              "Service saved but failed to update tax rates:",
              taxErr,
            );
          }
        }

        if (!isEditing && serviceId) {
          // Persist message toggles only when the user changed them off the
          // all-on default (the backend defaults to all-on anyway).
          if (hasNonDefaultMessageSettings(pendingMessageSettings)) {
            try {
              const url = getApiUrl(
                API_CONFIG.ENDPOINTS.SERVICE_MESSAGE_SETTINGS,
                "",
                { salonServiceUuid: serviceId },
              );
              await fetchWithAuth(url, {
                method: "PUT",
                body: JSON.stringify(pendingMessageSettings),
              });
            } catch (err) {
              console.error("Failed to save message settings:", err);
              notifyError(
                "Your message toggles were not saved. You can update them from the service editor.",
                { title: "Could not save message settings" },
              );
            }
          }

          for (const pf of pendingRequiredForms) {
            try {
              await linkRequiredForm(serviceId, {
                source_type: pf.source_type,
                source_id: pf.source_id,
              });
            } catch (err) {
              console.error("Failed to link required form:", err);
              notifyError(
                `"${pf.name}" was not saved. You can add it from the service editor.`,
                { title: "Could not add required form" },
              );
            }
          }

          for (const pd of pendingPreAppointmentDocuments) {
            try {
              await linkPreAppointmentDocument(serviceId, {
                source_type: pd.source_type,
                source_id: pd.source_id,
              });
            } catch (err) {
              console.error("Failed to attach pre-appointment document:", err);
              notifyError(
                `"${pd.name}" was not saved. You can attach it from the service editor.`,
                { title: "Could not attach document" },
              );
            }
          }

          // Skip the settings PUT when master is off (backend default).
          const postAppointmentDirty =
            pendingPostAppointmentSettings.master_enabled;
          if (postAppointmentDirty) {
            try {
              await updatePostAppointmentSettings(serviceId, {
                master_enabled: pendingPostAppointmentSettings.master_enabled,
              });
            } catch (err) {
              console.error("Failed to save post-appointment settings:", err);
              notifyError(
                "Post-appointment message settings were not saved. You can configure them from the service editor.",
                { title: "Could not save post-appointment messages" },
              );
            }
          }
          for (const msg of pendingPostAppointmentMessages) {
            try {
              await createPostAppointmentMessage(serviceId, {
                offset_value: msg.offset_value,
                offset_unit: msg.offset_unit,
                label: msg.label,
                attachments: msg.attachments.map((a, i) => ({
                  source_type: a.source_type,
                  source_id: a.source_id,
                  sort_order: i,
                })),
              });
            } catch (err) {
              console.error("Failed to create post-appointment message:", err);
              notifyError(
                "One of the new post-appointment messages was not saved. You can add it from the service editor.",
                { title: "Could not add post-appointment message" },
              );
            }
          }
        }

        const formattedResponse = formatResponse(response);
        onSuccess(formattedResponse);
        handleClose();
      } catch (error: any) {
        setErrors({ general: error.message || "Failed to save service" });
        console.error("Error saving service:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [
      formData,
      isEditing,
      businessInfo,
      employees,
      initialFormData,
      selectedAdjustmentFormIds,
      selectedTaxRateIds,
      pendingMessageSettings,
      pendingRequiredForms,
      pendingPreAppointmentDocuments,
      pendingPostAppointmentSettings,
      pendingPostAppointmentMessages,
      updatePostAppointmentSettings,
      createPostAppointmentMessage,
      formJunctions,
      showAdjustmentFormsStep,
      createServices,
      updateServices,
      deleteStaffService,
      setSalonServiceTaxRates,
      connectFormToServices,
      disconnectFormFromService,
      onSuccess,
      handleClose,
      hasStaffCustomization,
      fetchWithAuth,
      linkRequiredForm,
      linkPreAppointmentDocument,
    ],
  );

  const handleSave = useCallback(async () => {
    if (!validateStep(activeStep)) return;

    // If editing and default fields changed, show modal
    if (isEditing) {
      const changed = getChangedDefaultFields(initialFormData, formData);
      if (changed.length > 0) {
        setChangedFields(changed);
        setPendingSaveAction(() => () => performSave());
        setUpdateStaffModalOpened(true);
        return;
      }
    }

    // Otherwise, proceed with normal save
    await performSave();
  }, [
    activeStep,
    validateStep,
    isEditing,
    initialFormData,
    formData,
    performSave,
    getChangedDefaultFields,
  ]);

  const handleUpdateStaffModalConfirm = useCallback(
    async (option: "cancel" | "staff_without_customization" | "all_staff") => {
      setUpdateStaffModalOpened(false);

      if (option === "cancel") {
        setPendingSaveAction(null);
        setChangedFields([]);
        return;
      }

      if (pendingSaveAction) {
        await performSave(option);
        setPendingSaveAction(null);
        setChangedFields([]);
      }
    },
    [pendingSaveAction, performSave],
  );

  return (
    <>
      <Modal
        opened={opened}
        onClose={handleCloseAttempt}
        title={isEditing ? "Edit Service" : "Add New Service"}
        size="xl"
        fullScreen={isMobile}
        centered
        closeOnClickOutside={false}
        styles={{
          body: {
            display: "flex",
            flexDirection: "column",
            height: isMobile ? "calc(100dvh - 60px)" : "80vh",
            maxHeight: isMobile ? "calc(100dvh - 60px)" : "80vh",
            padding: 0,
          },
          content: {
            display: "flex",
            flexDirection: "column",
            maxHeight: isMobile ? "100dvh" : "90vh",
          },
        }}
      >
        <LoadingOverlay visible={isLoading} />

        {/* Fixed Stepper at top */}
        <Stepper
          active={activeStep}
          p="md"
          style={{
            width: "100%",
            margin: "0 auto",
          }}
        >
          <Stepper.Step
            label="Service Information"
            description="Basic details"
          />
          <Stepper.Step
            label="Additional Information"
            description="Forms, messaging & tax"
          />
          <Stepper.Step label="Performed By" description="Staff selection" />
          {showAdjustmentFormsStep && (
            <Stepper.Step
              label="Adjustment Forms"
              description="Optional customizations"
            />
          )}
        </Stepper>

        {/* Scrollable content area */}
        <Box
          style={{
            flex: 1,
            overflow: "auto",
            minHeight: 0,
            padding: "0 1rem 1rem 1rem",
          }}
        >
          {activeStep === 0 && (
            <ServiceInformationStep
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              serviceTypes={serviceTypes}
              isMobile={isMobile}
              showPricingBasis
            />
          )}

          {activeStep === 1 && (
            <AdditionalInformationStep
              serviceId={formData.id}
              selectedTaxRateIds={selectedTaxRateIds}
              onTaxRateChange={setSelectedTaxRateIds}
              isLoading={isLoading}
              messageSettings={pendingMessageSettings}
              onMessageSettingsChange={setPendingMessageSettings}
              pendingRequiredForms={pendingRequiredForms}
              onAddPendingForm={(form) =>
                setPendingRequiredForms((prev) => [...prev, form])
              }
              onRemovePendingForm={(tempId) =>
                setPendingRequiredForms((prev) =>
                  prev.filter((f) => f.tempId !== tempId),
                )
              }
              pendingPreAppointmentDocuments={pendingPreAppointmentDocuments}
              onAddPendingPreAppointmentDocument={(doc) =>
                setPendingPreAppointmentDocuments((prev) => [...prev, doc])
              }
              onRemovePendingPreAppointmentDocument={(tempId) =>
                setPendingPreAppointmentDocuments((prev) =>
                  prev.filter((d) => d.tempId !== tempId),
                )
              }
              postAppointmentSettings={pendingPostAppointmentSettings}
              onPostAppointmentSettingsChange={
                setPendingPostAppointmentSettings
              }
              pendingPostAppointmentMessages={pendingPostAppointmentMessages}
              onAddPendingPostAppointmentMessage={(message) =>
                setPendingPostAppointmentMessages((prev) => [...prev, message])
              }
              onUpdatePendingPostAppointmentMessage={(tempId, message) =>
                setPendingPostAppointmentMessages((prev) =>
                  prev.map((p) => (p.tempId === tempId ? message : p)),
                )
              }
              onRemovePendingPostAppointmentMessage={(tempId) =>
                setPendingPostAppointmentMessages((prev) =>
                  prev.filter((p) => p.tempId !== tempId),
                )
              }
            />
          )}

          {activeStep === 2 && (
            <StaffSelectionStep
              formData={formData}
              setFormData={setFormData}
              employees={activeEmployees || []}
              isMobile={isMobile}
            />
          )}

          {activeStep === 3 && showAdjustmentFormsStep && (
            <AdjustmentFormsStep
              adjustmentForms={adjustmentForms}
              selectedFormIds={selectedAdjustmentFormIds}
              setSelectedFormIds={setSelectedAdjustmentFormIds}
              isMobile={isMobile}
            />
          )}

          {errors.general && (
            <Text c="red" mt="md">
              {errors.general}
            </Text>
          )}
        </Box>

        {/* Fixed navigation buttons at bottom */}
        <Group
          justify="space-between"
          p="md"
          style={{
            borderTop: "1px solid var(--mantine-color-gray-3)",
            flexShrink: 0,
          }}
        >
          <Button
            variant="subtle"
            onClick={handleBack}
            disabled={activeStep === 0}
            style={{
              opacity: activeStep === 0 ? "0" : "1",
              cursor: activeStep === 0 ? "default" : "pointer",
            }}
          >
            Back
          </Button>

          {activeStep === totalSteps - 1 ? (
            <Button color="teal" onClick={handleSave} loading={isLoading}>
              Save
            </Button>
          ) : (
            <Button color="teal" onClick={handleNext}>
              Next
            </Button>
          )}
        </Group>
      </Modal>

      <DeleteConfirmationModal
        isOpen={showCloseConfirmation}
        onClose={() => setShowCloseConfirmation(false)}
        onConfirm={handleClose}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to close? All changes will be lost."
      />

      <UpdateStaffServicesModal
        opened={updateStaffModalOpened}
        onClose={() => {
          setUpdateStaffModalOpened(false);
          setPendingSaveAction(null);
          setChangedFields([]);
        }}
        onConfirm={handleUpdateStaffModalConfirm}
        changedFields={changedFields}
      />
    </>
  );
}
